import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Switch from "react-switch";
import { toast } from "react-toastify";
import RightArrow from "../../assets/svg/RightArrow";
import styles from "./Privacy.module.scss";
import axios from "../../components/Hooks/axios";
import { setUserObject } from "../../redux/slices/authSlice";
import searchIcon from "../../assets/svg/search.svg";
import { FaAngleLeft } from "react-icons/fa6";

const Post = ({ isMobile }) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const [activeCategory, setActiveCategory] = useState(null); // 'view', 'like_and_comment', 'sharing'
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [disableSwitch, setDisableSwitch] = useState(false);

    const [privacy, setPrivacy] = useState({
        view: { option: "everyone", specific_users: [] },
        like_and_comment: { option: "everyone", specific_users: [] },
        sharing: { option: "everyone", specific_users: [] },
        likes_views: { option: "everyone", specific_users: [] }
    });

    const [followers, setFollowers] = useState([]);

    useEffect(() => {
        axios.get("/auth/follower-following").then(({ data }) => {
            setFollowers(data?.data?.followersData || []);
        });
    }, []);

    useEffect(() => {
        if (user) {
            setPrivacy({
                view: user.post_privacy?.view || { option: "everyone", specific_users: [] },
                like_and_comment: user.post_privacy?.like_and_comment || { option: "everyone", specific_users: [] },
                sharing: user.post_privacy?.sharing || { option: "everyone", specific_users: [] },
                likes_views: user.likes_views_privacy || { option: "everyone", specific_users: [] }
            });
        }
    }, [user]);

    const handleOptionChange = async (category, option) => {
        const newPrivacy = {
            ...privacy,
            [category]: {
                ...privacy[category],
                option
            }
        };
        setPrivacy(newPrivacy);

        const id = toast.loading("Updating...");
        setDisableSwitch(true);
        try {
            let endpoint = "/auth/change-post-privacy";
            let payload;

            if (category === "likes_views") {
                payload = { likes_views_privacy: newPrivacy[category] };
            } else {
                payload = {
                    post_privacy: {
                        view: newPrivacy.view,
                        like_and_comment: newPrivacy.like_and_comment,
                        sharing: newPrivacy.sharing
                    }
                };
            }

            const { data } = await axios.post(endpoint, payload);
            dispatch(setUserObject(data.data));
            toast.update(id, { render: "Updated", type: "success", isLoading: false, autoClose: 2000 });
        } catch (error) {
            console.error(error);
            toast.update(id, { render: "Error", type: "error", isLoading: false, autoClose: 2000 });
        } finally {
            setDisableSwitch(false);
        }
    };

    const handleSearch = (query) => {
        setSearchTerm(query);
        if (query.trim() === "") {
            setSearchResults([]);
            return;
        }
        const filtered = followers.filter(f =>
            f.username?.toLowerCase().includes(query.toLowerCase()) ||
            f.displayName?.toLowerCase().includes(query.toLowerCase())
        );
        setSearchResults(filtered);
    };

    const toggleUser = async (category, selectedUser) => {
        const currentUsers = privacy[category].specific_users || [];
        const isSelected = currentUsers.some(u => (u._id || u) === selectedUser._id);

        let newUsers;
        if (isSelected) {
            newUsers = currentUsers.filter(u => (u._id || u) !== selectedUser._id);
        } else {
            newUsers = [...currentUsers, selectedUser._id];
        }

        const newPrivacy = {
            ...privacy,
            [category]: {
                ...privacy[category],
                specific_users: newUsers
            }
        };
        setPrivacy(newPrivacy);

        // Persist the change
        const id = toast.loading("Updating specific users...");
        setDisableSwitch(true);
        try {
            let endpoint = "/auth/change-post-privacy";
            let payload;

            if (category === "likes_views") {
                payload = { likes_views_privacy: newPrivacy[category] };
            } else {
                payload = {
                    post_privacy: {
                        view: newPrivacy.view,
                        like_and_comment: newPrivacy.like_and_comment,
                        sharing: newPrivacy.sharing
                    }
                };
            }

            const { data } = await axios.post(endpoint, payload);
            dispatch(setUserObject(data.data));
            toast.update(id, { render: "Updated", type: "success", isLoading: false, autoClose: 2000 });
        } catch (error) {
            toast.update(id, { render: "Error", type: "error", isLoading: false, autoClose: 2000 });
        } finally {
            setDisableSwitch(false);
        }
    };

    const getOptionLabel = (option) => {
        switch (option) {
            case "everyone": return "Everyone";
            case "followers": return "Your Followers";
            case "followers_except": return "Followers Except...";
            case "no_one_except": return "No One Except...";
            default: return "Everyone";
        }
    };

    const renderCategoryItem = (id, title, category) => (
        <div className={styles.InnerDiv} key={id}>
            <h5>{title}</h5>
            <span onClick={() => setActiveCategory(activeCategory === category ? null : category)}>
                <p>{getOptionLabel(privacy[category].option)}</p>
                <div style={{ transform: activeCategory === category ? 'rotate(90deg)' : 'none', transition: '0.3s' }}>
                    <RightArrow />
                </div>
            </span>
        </div>
    );

    const renderOptions = (category) => {
        const options = [
            { id: "everyone", label: "Everyone" },
            { id: "followers", label: "Your Followers" },
            { id: "followers_except", label: "Your Followers Except" },
            { id: "no_one_except", label: "No One Except" }
        ];

        return (
            <div className={styles.AllowCommentsFrom} style={{ marginTop: '1rem', borderTop: 'none' }}>
                <p className={styles.InteractionsText}>Allow {category === 'view' ? 'views' : category === 'like_and_comment' ? 'likes & comments' : 'sharing'} from</p>
                {options.map((opt) => (
                    <div className={styles.OptionRow} key={opt.id}>
                        <div className={styles.OptionHeader}>
                            <h2 className={styles.OptionLabel}>{opt.label}</h2>
                            <Switch
                                onChange={(checked) => checked && handleOptionChange(category, opt.id)}
                                checked={privacy[category].option === opt.id}
                                disabled={disableSwitch}
                                offColor={"#F2DBF1"}
                                onColor={"#F2DBF1"}
                                offHandleColor={"#A6539D"}
                                onHandleColor={"#A6539D"}
                                uncheckedIcon={false}
                                checkedIcon={false}
                                activeBoxShadow={"0 0 0px 0px #909AFF"}
                                height={isMobile ? 30 : 14}
                                width={isMobile ? 64 : 33}
                            />
                        </div>
                        {opt.id === 'followers' && (
                            <p className={styles.FollowerCount}>
                                {followers.length} Followers
                            </p>
                        )}
                    </div>
                ))}

                {(privacy[category].option === "followers_except" || privacy[category].option === "no_one_except") && (
                    <div style={{ marginTop: '1rem', borderTop: '1px solid #eee', paddingTop: '1rem' }}>
                        <div className={styles.SubPageSearchContainer}>
                            <img src={searchIcon} alt="Search" style={{ marginRight: '10px', width: '16px' }} />
                            <input
                                type="text"
                                placeholder="Search followers..."
                                value={searchTerm}
                                onChange={(e) => handleSearch(e.target.value)}
                                className={styles.SubPageSearchInput}
                            />
                        </div>
                        <div className={`hide-scrollbar ${styles.SubPageListScroll}`}>
                            {(() => {
                                const listToDisplay = searchTerm.trim() === "" ? followers : searchResults;
                                if (listToDisplay.length === 0) {
                                    return (
                                        <div style={{ textAlign: 'center', padding: '20px', color: '#999', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <p style={{ fontSize: '0.9rem', textTransform: 'none' }}>Follower not found</p>
                                        </div>
                                    );
                                }
                                return listToDisplay.map(u => {
                                    const currentUsers = privacy[category].specific_users || [];
                                    const isSelected = currentUsers.some(su => (su._id || su) === u._id);
                                    const isChecked = privacy[category].option === "followers_except" ? !isSelected : isSelected;

                                    return (
                                        <div
                                            key={u._id}
                                            onClick={() => toggleUser(category, u)}
                                            className={styles.UserListItem}
                                        >
                                            <div className={styles.UserInfo}>
                                                <img 
                                                    src={u.ProfilePic} 
                                                    alt="" 
                                                    className={styles.UserProfilePic}
                                                    onError={(e) => {
                                                        e.target.src = "https://cdn-icons-png.flaticon.com/512/149/149071.png";
                                                    }}
                                                />
                                                <span className={styles.UserName}>{u.displayName || u.username}</span>
                                            </div>
                                            <div
                                                className={`${styles.CustomCheckbox} ${isChecked ? styles.checked : ''}`}
                                            >
                                                {isChecked && <span className={styles.CheckIcon}>✓</span>}
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={styles.PostContainer}>
            <div className={styles.PostContainerInner} style={{ height: 'auto', paddingBottom: '2rem' }}>
                <h1>Post Privacy Options</h1>
                <p style={{ color: '#959595', marginBottom: '2rem' }}>Manage who can view, like, comment, and share your posts.</p>

                {renderCategoryItem(1, "View Options", "view")}
                {activeCategory === "view" && renderOptions("view")}

                {renderCategoryItem(2, "Like and Comment Options", "like_and_comment")}
                {activeCategory === "like_and_comment" && renderOptions("like_and_comment")}

                {renderCategoryItem(3, "Post Sharing Options", "sharing")}
                {activeCategory === "sharing" && renderOptions("sharing")}
            </div>
        </div>
    );
};

export default Post;


